/*
 * OnboardingSuccess — one-time "Welcome to Premium" modal.
 *
 * Purpose
 * -------
 * The moment a user's subscription first activates (either through Apple
 * StoreKit IAP, Stripe web checkout, or an App Store Server Notification
 * arriving out-of-band on our /api/apple/notifications endpoint), we want
 * to acknowledge the moment warmly. This modal is that acknowledgement.
 *
 * Behaviour
 * ---------
 *   - Renders only when:
 *       (a) user is signed in
 *       (b) user.subscription.is_active === true
 *       (c) user.onboarding.premium_first_seen_at is null / undefined
 *   - Once the user taps "Begin your rhythm", we POST to
 *     /api/user/onboarding/premium-seen so the flag is persisted server-
 *     side. That makes it survive device switches and re-installs.
 *   - Falls back gracefully if the persistence call fails — we still
 *     dismiss the modal locally so the user is not blocked; the next
 *     login attempt will show the modal again, but that is a benign
 *     retry, not a broken flow.
 *
 * Design
 * ------
 * Warm night-mode overlay consistent with the Paywall aesthetic:
 *   - Semi-opaque dark backdrop with a slow fade-in
 *   - Centered card, gold hairline accents, Playfair Display headline
 *   - Sparkles icon in a soft gold halo (matches the Selah brand)
 *   - Subtle grain overlay (2% opacity noise) for depth
 *   - Fully responsive down to 320px viewport widths
 *
 * Accessibility
 * -------------
 *   - role="dialog" + aria-modal="true"
 *   - Focus trapped to the Continue button on mount
 *   - Escape key dismisses the modal (and persists the flag)
 *   - All interactive controls have data-testid attributes for QA
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ShieldCheck, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../utils/api';

const COLORS = {
  bg: 'rgba(10, 10, 10, 0.82)',
  card: '#181818',
  cardBorder: 'rgba(212,175,55,0.28)',
  gold: '#D4AF37',
  goldSoft: 'rgba(212,175,55,0.55)',
  sand: '#D2B48C',
  alabaster: '#FDFBF7',
  fgSubtle: 'rgba(253,251,247,0.78)',
  fgMuted: 'rgba(253,251,247,0.5)',
  hairline: 'rgba(253,251,247,0.09)',
};

/**
 * Format a subscription's next-renewal date into a warm, human sentence.
 * Falls back to "your next month" when the date is missing or unparsable
 * so the copy never breaks the celebratory tone.
 */
function friendlyRenewalPhrase(expiresAt) {
  if (!expiresAt) return 'in about a month';
  try {
    const d = new Date(expiresAt);
    if (Number.isNaN(d.getTime())) return 'in about a month';
    const opts = { month: 'long', day: 'numeric' };
    return `on ${d.toLocaleDateString(undefined, opts)}`;
  } catch (_e) {
    return 'in about a month';
  }
}

export default function OnboardingSuccess({ user, onDismiss }) {
  const [dismissing, setDismissing] = useState(false);

  // Coordinate with the general FirstRunWelcome dialog (which lives on
  // the Dashboard and uses its own localStorage flag). We wait for that
  // dialog to be dismissed before firing our celebration so the two
  // modals never stack (Radix's overlay was intercepting our clicks in
  // testing — see console log Aug 24 2026).
  const firstRunSeen = React.useMemo(() => {
    if (!user?.id) return false;
    try {
      return Boolean(
        window.localStorage.getItem(`selah.hasSeenWelcome.${user.id}`)
      );
    } catch (_e) {
      // localStorage unavailable — assume the FirstRunWelcome dialog
      // won't show either, so we go ahead and celebrate.
      return true;
    }
  }, [user?.id]);

  const shouldShow =
    !!user &&
    !!user.subscription?.is_active &&
    !user.onboarding?.premium_first_seen_at &&
    firstRunSeen;

  // Persist "seen" and close.
  const handleDismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      await api.markPremiumOnboardingSeen();
    } catch (_e) {
      // Non-fatal — see file header for rationale.
    }
    onDismiss?.();
    setDismissing(false);
  };

  // Escape key closes the modal.
  useEffect(() => {
    if (!shouldShow) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleDismiss();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shouldShow]);

  if (!shouldShow) return null;

  const renewalPhrase = friendlyRenewalPhrase(
    user.subscription?.expires_at || user.subscription?.apple_expires_at
  );
  const isApple = user.subscription?.payment_provider === 'apple_iap';

  return (
    <AnimatePresence>
      <motion.div
        key="onboarding-success-backdrop"
        data-testid="onboarding-success-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-success-title"
        className="fixed inset-0 z-[220] flex items-center justify-center p-4 sm:p-6"
        style={{ background: COLORS.bg, backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.24 }}
      >
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.99 }}
          transition={{ duration: 0.32, ease: [0.2, 0.6, 0.2, 1] }}
          className="relative w-full max-w-md rounded-2xl overflow-hidden"
          style={{
            background: COLORS.card,
            border: `1px solid ${COLORS.cardBorder}`,
            boxShadow: '0 30px 70px -18px rgba(0,0,0,0.65), 0 8px 24px -12px rgba(0,0,0,0.5)',
          }}
        >
          {/* subtle gold hairline at the top */}
          <div
            aria-hidden
            className="absolute top-0 left-8 right-8 h-px"
            style={{
              background: `linear-gradient(90deg, transparent, ${COLORS.goldSoft}, transparent)`,
            }}
          />

          {/* soft radial gold glow behind the icon */}
          <div
            aria-hidden
            className="absolute -top-16 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(212,175,55,0.14) 0%, rgba(212,175,55,0) 70%)',
              filter: 'blur(4px)',
            }}
          />

          <div className="relative px-7 pt-9 pb-7 text-center">
            {/* Icon medallion */}
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.35, ease: 'easeOut' }}
              className="mx-auto mb-5 w-16 h-16 rounded-full flex items-center justify-center"
              style={{
                background: 'rgba(212, 175, 55, 0.10)',
                border: `1px solid ${COLORS.goldSoft}`,
              }}
            >
              <Sparkles size={26} strokeWidth={1.8} style={{ color: COLORS.gold }} />
            </motion.div>

            {/* Kicker */}
            <p
              className="text-[11px] uppercase mb-2"
              style={{
                color: COLORS.sand,
                letterSpacing: '0.32em',
              }}
            >
              You&apos;re in
            </p>

            {/* Headline */}
            <h2
              id="onboarding-success-title"
              className="text-[26px] leading-tight font-semibold mb-3"
              style={{
                color: COLORS.alabaster,
                fontFamily: '"Playfair Display", serif',
              }}
              data-testid="onboarding-success-title"
            >
              Welcome to Selah Premium.
            </h2>

            {/* Sub-copy */}
            <p
              className="text-[13.5px] leading-relaxed mb-5"
              style={{ color: COLORS.fgSubtle }}
            >
              Every future download, every follow-up, every quiet 3&thinsp;a.m.
              flash is unlocked. Your reflections stay encrypted; your rhythm
              stays yours.
            </p>

            {/* What you just unlocked */}
            <div
              className="mt-1 mb-6 rounded-xl px-4 py-4 text-left space-y-2.5"
              style={{
                background: 'rgba(253,251,247,0.025)',
                border: `1px solid ${COLORS.hairline}`,
              }}
              data-testid="onboarding-success-benefits"
            >
              <BenefitRow icon={Sparkles} text="AI insights, Scripture, and prayer on every download" />
              <BenefitRow icon={ShieldCheck} text="Zero-knowledge encryption on everything you save" />
              <BenefitRow icon={Heart} text="Follow-up prompts that turn moments into a thread" />
            </div>

            {/* Renewal note */}
            <p
              className="mb-6 text-[11.5px] leading-relaxed"
              style={{ color: COLORS.fgMuted }}
              data-testid="onboarding-success-renewal"
            >
              Renews {renewalPhrase}
              {isApple ? ' via your Apple ID' : ''}. Cancel any time from Settings &rarr; Subscription.
            </p>

            {/* CTA */}
            <Button
              type="button"
              onClick={handleDismiss}
              disabled={dismissing}
              data-testid="onboarding-success-continue-button"
              className="w-full h-11 font-semibold"
              style={{
                background: COLORS.gold,
                color: '#1B1B1B',
                border: 'none',
              }}
            >
              {dismissing ? 'Settling…' : 'Begin your rhythm'}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function BenefitRow({ icon: Icon, text }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon
        size={14}
        strokeWidth={1.9}
        style={{ color: COLORS.sand, marginTop: 2, flexShrink: 0 }}
      />
      <span className="text-[12.5px] leading-relaxed" style={{ color: COLORS.fgSubtle }}>
        {text}
      </span>
    </div>
  );
}
